import Highcharts from 'highcharts'
import { useMemo } from 'react'
import { useTheme } from '@mui/styles'
import { Activity } from '../../../shared/queries/useGetActivitiesQuery'
import { useIsDarkMode } from '../../../shared/hooks/useIsDarkMode'
import { toDisplayValue } from '../../../shared/utils/displayValue'
import {
    ActivityDateRange,
    ActivityDateType,
    PLANNED_BAR_ALPHA,
    formatQuarterUTC,
    hexWithAlpha,
    resolveActivityDateRange,
} from '../../../shared/utils/resolveActivityTimelineDates'

interface TppGanttSection {
    id: string
    title: string
    color: string
}

interface GanttChartActivity {
    name: string
    sectionTitle: string
    activityCategory: string
    start: number
    end: number
    budget: string
    startDateType: ActivityDateType
    endDateType: ActivityDateType
    isPlanned: boolean
    color?: string
}

const useTppGanttChartOptions = (
    activitiesBySection: Record<string, Activity[]>,
    sections: TppGanttSection[],
    isInteractive: boolean
): Highcharts.Options | null => {
    const theme = useTheme()
    const isDarkMode = useIsDarkMode()

    // Extract theme values to use as stable dependencies
    const themeColors = useMemo(
        () => ({
            tier7: theme.palette.figma.grayscale.tier7,
            contentWrapper: theme.palette.figma.grayscale.contentWrapper,
            textHier1: theme.palette.figma.grayscale.textHier1,
            stroke: theme.palette.figma.grayscale.stroke,
            white: theme.palette.figma.accentColors.white,
        }),
        [
            theme.palette.figma.grayscale.tier7,
            theme.palette.figma.grayscale.contentWrapper,
            theme.palette.figma.grayscale.textHier1,
            theme.palette.figma.grayscale.stroke,
            theme.palette.figma.accentColors.white,
        ]
    )

    const ganttChartOptions = useMemo((): Highcharts.Options | null => {
        if (sections.length === 0) {
            return null
        }

        const formatActivityData = (
            activity: Activity,
            sectionTitle: string,
            dateRange: ActivityDateRange
        ): GanttChartActivity => ({
            name: activity.title,
            sectionTitle,
            activityCategory: activity.category,
            start: dateRange.start,
            end: dateRange.end,
            budget: activity.budget,
            startDateType: dateRange.startDateType,
            endDateType: dateRange.endDateType,
            isPlanned: dateRange.isPlanned,
        })

        const sortActivities = (activities: GanttChartActivity[]): GanttChartActivity[] => {
            return [...activities].sort((a, b) => a.end - b.end)
        }

        const seriesArray: Highcharts.SeriesOptionsType[] = sections.map((section) => {
            const sectionActivities = activitiesBySection[section.id] || []
            // Activities fall back to their planned dates; a null range means neither
            // end resolved to a usable date, so there's no bar to draw.
            const formattedActivities = sectionActivities.reduce((acc, activity) => {
                const dateRange = resolveActivityDateRange(activity)
                if (dateRange) acc.push(formatActivityData(activity, section.title, dateRange))

                return acc
            }, [] as GanttChartActivity[])

            return {
                id: section.id,
                name: section.title,
                // Planned-derived bars render as a translucent version of their
                // section color so forecast timelines read as distinct from actuals.
                data: sortActivities(formattedActivities).map((activity) =>
                    activity.isPlanned
                        ? { ...activity, color: hexWithAlpha(section.color, PLANNED_BAR_ALPHA) }
                        : activity
                ),
                colorByPoint: false,
                color: section.color,
                borderColor: themeColors.tier7,
                borderWidth: 0,
                borderRadius: 6,
                enableMouseTracking: isInteractive,
                dataLabels: {
                    color: themeColors.white,
                    style: {
                        textOutline: isDarkMode ? 'none' : `1px solid ${themeColors.textHier1}`,
                        fontWeight: '700',
                        fontSize: '12px',
                        fontFamily: 'acumin-pro',
                    },
                },
                type: 'gantt',
            }
        })

        // No renderable data after filtering out activities without dates
        const hasData = seriesArray.some((s) => (s as { data: unknown[] }).data.length > 0)
        if (!hasData) return null

        return {
            chart: {
                backgroundColor: 'transparent',
                reflow: true,
                marginLeft: 300,
                animation: false,
                events: {
                    load: function () {
                        const container = this.container
                        container.addEventListener(
                            'mouseenter',
                            (e: MouseEvent) => {
                                const target = e.target as HTMLElement
                                const labelEl = target.closest('.gantt-y-label') as HTMLElement | null
                                if (!labelEl) return
                                // Only show tooltip if text is actually truncated
                                if (labelEl.scrollWidth <= labelEl.clientWidth) return
                                const tooltip = labelEl.querySelector('.gantt-y-label-tooltip') as HTMLElement | null
                                if (!tooltip) return
                                const rect = labelEl.getBoundingClientRect()
                                tooltip.style.display = 'block'
                                tooltip.style.left = `${rect.left}px`
                                tooltip.style.top = `${rect.top - tooltip.offsetHeight - 6}px`
                            },
                            true
                        )
                        container.addEventListener(
                            'mouseleave',
                            (e: MouseEvent) => {
                                const target = e.target as HTMLElement
                                const labelEl = target.closest('.gantt-y-label') as HTMLElement | null
                                if (!labelEl) return
                                const tooltip = labelEl.querySelector('.gantt-y-label-tooltip') as HTMLElement | null
                                if (tooltip) tooltip.style.display = 'none'
                            },
                            true
                        )
                    },
                },
            },
            credits: {
                enabled: false,
            },
            tooltip: {
                enabled: isInteractive,
                backgroundColor: themeColors.contentWrapper,
                padding: 12,
                borderRadius: 8,
                borderWidth: 0,
                shadow: false,
                useHTML: true,
                formatter: function () {
                    const point = this.point as unknown as GanttChartActivity
                    const start = new Date(this.x)
                    const end = this.point.x2 ? new Date(this.point.x2) : null

                    const plannedMarker = (dateType: ActivityDateType) => (dateType === 'planned' ? ' (planned)' : '')

                    const containerStyle = `font-family: acumin-pro, sans-serif; color: ${themeColors.textHier1};`
                    const titleStyle = `font-size: 14px; font-weight: 700; text-align: center; margin-bottom: 6px;`
                    const rowStyle = `display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; font-size: 13px;`
                    const labelStyle = `font-weight: 600; margin-right: 12px;`
                    const hrStyle = `border: 0; border-top: 1px solid ${themeColors.stroke}; margin: 8px 0;`

                    return `
                        <div style="${containerStyle}">
                            <div style="${titleStyle}">${toDisplayValue(point.sectionTitle)}</div>
                            <div style="${rowStyle}">
                                <span style="${labelStyle}">Budget:</span>
                                <span>$${point.budget}</span>
                            </div>
                            <div style="${rowStyle}">
                                <span style="${labelStyle}">Category:</span>
                                <span>${toDisplayValue(point.activityCategory)}</span>
                            </div>
                            <hr style="${hrStyle}" />
                            <div style="${rowStyle}">
                                <span style="${labelStyle}">Start:</span>
                                <span>${formatQuarterUTC(start)}${plannedMarker(point.startDateType)}</span>
                            </div>
                            <div style="${rowStyle}">
                                <span style="${labelStyle}">End:</span>
                                <span>${end ? `${formatQuarterUTC(end)}${plannedMarker(point.endDateType)}` : 'N/A'}</span>
                            </div>
                        </div>
                    `
                },
            },
            xAxis: [
                {
                    grid: {
                        cellHeight: 30,
                    },
                    lineColor: themeColors.stroke,
                    tickColor: themeColors.stroke,
                    type: 'datetime',
                    units: [['month', [3, 6]]],
                    labels: {
                        formatter: function () {
                            // @ts-expect-error copypaste old code
                            if (this.target.isFirst) {
                                const quarter = Math.ceil((new Date(this.value).getMonth() + 1) / 3)
                                // @ts-expect-error copypaste old code
                                return Highcharts.dateFormat('Q' + quarter, this.value)
                            }
                            let quarter = Math.ceil((new Date(this.value).getMonth() + 1) / 3) + 1
                            quarter = quarter === 5 ? 1 : quarter
                            // @ts-expect-error copypaste old code
                            return Highcharts.dateFormat('Q' + quarter, this.value)
                        },
                        align: 'center',
                        style: {
                            color: themeColors.textHier1,
                            fontSize: '16px',
                            fontWeight: '400',
                            fontFamily: 'acumin-pro',
                        },
                    },
                    showLastLabel: false,
                },
                {
                    grid: {
                        cellHeight: 24,
                    },
                    lineColor: themeColors.stroke,
                    tickColor: themeColors.stroke,
                    type: 'datetime',
                    labels: {
                        align: 'center',
                        style: {
                            color: themeColors.textHier1,
                            fontSize: '16px',
                            fontWeight: '700',
                            fontFamily: 'acumin-pro',
                        },
                    },
                },
            ],
            yAxis: {
                lineColor: themeColors.stroke,
                tickColor: themeColors.stroke,
                gridLineColor: themeColors.stroke,
                labels: {
                    useHTML: true,
                    formatter: function () {
                        const label = this.value as string
                        const escaped = label.replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
                        return `<span class="gantt-y-label" style="display:block; max-width:280px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; position:relative;">
                            ${escaped}
                            <span class="gantt-y-label-tooltip" style="
                                display:none;
                                position:fixed;
                                z-index:1500;
                                background-color:${themeColors.tier7};
                                color:${themeColors.textHier1};
                                padding:4px 16px;
                                border-radius:6px;
                                box-shadow:0px 4px 8px 0px rgba(0,0,0,0.20);
                                font-size:14px;
                                font-weight:400;
                                font-family:acumin-pro,sans-serif;
                                white-space:pre-line;
                                max-width:400px;
                                pointer-events:none;
                            ">${escaped}</span>
                        </span>`
                    },
                    style: { color: themeColors.textHier1 },
                    padding: 4,
                },
            },
            series: seriesArray,
            navigator: {
                enabled: true,
                liveRedraw: true,
                adaptToUpdatedData: true,
                margin: 0,
                series: {
                    // @ts-expect-error weird typing
                    type: 'gantt',
                    pointPlacement: 0.5,
                    pointPadding: 0.25,
                },
                yAxis: {
                    min: 0,
                    max: 11,
                    reversed: true,
                },
            },
            plotOptions: {
                series: {
                    animation: false,
                },
                gantt: {
                    pointPadding: 0,
                    animation: false,
                    dataLabels: {
                        enabled: true,
                        animation: false,
                        formatter: function (this: Highcharts.PointLabelObject): string {
                            const point = this.point as unknown as GanttChartActivity
                            return point.sectionTitle || ''
                        },
                        style: {
                            color: themeColors.textHier1,
                        },
                    },
                },
            },
        }
    }, [activitiesBySection, sections, isInteractive, themeColors, isDarkMode])

    return ganttChartOptions
}

export default useTppGanttChartOptions
